/**
 * Beyond Sure — mock streaming reply engine.
 *
 * NOT wired to any backend yet. Simulates token-by-token streaming the
 * way a real LLM (Claude, etc.) would, so the rest of the UI can be
 * built against the real shape today. The chat surface treats the
 * `streaming: true` flag as live and the final emission as done.
 *
 * Phase 3 replaces this file with a real fetch+ReadableStream against
 * `/api/chat/message`. Keep the public surface stable so the swap is
 * mechanical.
 *
 * // TODO: replace mockStream() with a real SSE/fetch-stream call to
 *   /api/chat/message that yields { delta } chunks.
 */

import type {
  Citation,
  Message,
  MessageBlock,
  OcrResultData,
} from "./types";

interface MockReply {
  /** Optional "Thinking…" summary the model emits first. */
  thinking?: string;
  /** Markdown body — chunked into ~3-char slices for token feel. */
  body: string;
  /** Optional rich blocks shown after the body finishes streaming. */
  blocks?: MessageBlock[];
  /** Optional RAG citations rendered as chips under the answer. */
  citations?: Citation[];
}

/**
 * Shared mock citation set — these reads SHOULD become real RAG
 * citations once the RAG brain is wired (Phase 3+).
 * // TODO: replace with real citations from RAG layer.
 */
const REGULATOR_IRDAI: Citation = {
  label: "IRDAI Annual Report 2024 — claim ratios",
  url: "https://irdai.gov.in/annual-reports",
  kind: "regulator",
};
const REGULATOR_OMBUDSMAN: Citation = {
  label: "Insurance Ombudsman — appeal precedent",
  url: "https://cioins.co.in/Ombudsman",
  kind: "regulator",
};
const INSURER_BROCHURE: Citation = {
  label: "Insurer brochure — sub-limits + waiting periods",
  url: "https://www.beyondsure.in/sources",
  kind: "insurer",
};
const INTERNAL_KNOWLEDGE: Citation = {
  label: "BeyondSure knowledge — policy comparison engine",
  url: "https://www.beyondsure.in/methodology",
  kind: "internal",
};

/**
 * Very small heuristic: looks at the user prompt for keywords and
 * returns a tailored mock reply. Phase 2 will add rich blocks
 * (recommendation cards, comparison tables) — for Phase 1 the body
 * is text-only with light markdown.
 */
export function pickMockReply(userPrompt: string): MockReply {
  const q = userPrompt.toLowerCase();

  if (q.includes("compare") || q.includes("plan") || q.includes("shortlist")) {
    return {
      thinking:
        "Looking at the user's profile (age 32, family of 4, ₹25-50k budget). " +
        "Pulling claim-settlement ratios from IRDAI Annual Report 2024. " +
        "Filtering for Bangalore network hospitals.",
      body:
        "Based on your profile, I compared **6 insurers** for you. Here's the side-by-side, " +
        "ranked by fit — and the **one** I'd pick if I were you.",
      blocks: [
        {
          kind: "comparison",
          data: {
            query: "Family floater · ₹10L cover · age 32 · BLR",
            rows: [
              {
                insurer: "HDFC Optima Restore",
                premium: "₹11,400",
                cover: "₹10 L",
                claimRatio: "98.2%",
                fit: 92,
              },
              {
                insurer: "Niva Bupa ReAssure 2.0",
                premium: "₹12,900",
                cover: "₹10 L",
                claimRatio: "96.8%",
                fit: 86,
              },
              {
                insurer: "ICICI Lombard Complete Health",
                premium: "₹10,200",
                cover: "₹10 L",
                claimRatio: "95.4%",
                fit: 81,
              },
              {
                insurer: "Bajaj Allianz Health Guard",
                premium: "₹9,800",
                cover: "₹10 L",
                claimRatio: "93.1%",
                fit: 72,
              },
              {
                insurer: "TATA AIG MediCare",
                premium: "₹10,900",
                cover: "₹10 L",
                claimRatio: "91.5%",
                fit: 64,
              },
              {
                insurer: "Star Health Family Optima",
                premium: "₹9,400",
                cover: "₹10 L",
                claimRatio: "89.7%",
                fit: 55,
              },
            ],
          },
        },
        {
          kind: "recommendation",
          data: {
            planName: "Optima Restore",
            insurer: "HDFC ERGO",
            cover: "₹10 L",
            premium: "₹11,400 / yr",
            claimRatio: "98.2%",
            fitReason:
              "Same-day cashless at 5,000+ hospitals, restore benefit if cover exhausts mid-year, no room-rent capping — exactly what a family of four needs when one hospitalisation can wipe out the year's budget.",
          },
        },
      ],
      citations: [REGULATOR_IRDAI, INSURER_BROCHURE, INTERNAL_KNOWLEDGE],
    };
  }

  // Specifically when the user has JUST uploaded — we infer that
  // upstream and rewrite the prompt to include "uploaded" + filename.
  if (q.includes("uploaded") && (q.includes(".pdf") || q.includes(".jpg") || q.includes(".png"))) {
    const fileName = inferFileName(userPrompt);
    return {
      thinking:
        "Document received. Running OCR (text extraction). Cross-checking the " +
        "extracted policy schedule against IRDAI's published claim data and our " +
        "internal policy structure index. Looking for sub-limits, waiting " +
        "periods, room-rent capping, exclusions.",
      body:
        "I've read your policy end-to-end. Here's the **truth-audit** — what's " +
        "in the cover, what's missing, and what to watch.",
      blocks: [
        {
          kind: "ocr-result",
          data: mockOcrResult(fileName),
        },
        {
          kind: "truth-audit",
          data: {
            policyName: "TATA AIG MediCare Premier · 2024",
            findings: [
              {
                label: "Cashless network",
                detail: "5,200+ hospitals · all major BLR network partners covered.",
                severity: "ok",
              },
              {
                label: "Claim-settlement ratio",
                detail: "91.5% (IRDAI FY24, verified) — below the 95% peer median.",
                severity: "warn",
              },
              {
                label: "Pre-existing waiting period",
                detail: "3 years for declared diabetes — fine if you have time, costly to claim early.",
                severity: "warn",
              },
              {
                label: "Room-rent capping",
                detail: "1% of sum insured per day. On ₹10L cover that's ₹10,000 — most private rooms in BLR exceed this.",
                severity: "block",
              },
              {
                label: "No-claim bonus",
                detail: "+50% cover at each claim-free year, max 100% — strong long-term feature.",
                severity: "ok",
              },
            ],
          },
        },
      ],
      citations: [REGULATOR_IRDAI, INSURER_BROCHURE],
    };
  }

  if (q.includes("audit") || q.includes("policy") || q.includes("check")) {
    return {
      thinking:
        "User wants me to read an existing policy and flag what's worth knowing. " +
        "Pulling typical policy structure (sub-limits, waiting periods, exclusions).",
      body:
        "Happy to audit it. Upload the PDF (or a photo) using the attach button below " +
        "and I'll:\n\n" +
        "- Extract every covered + excluded condition\n" +
        "- Cross-check the claim-settlement ratio against the regulator's own data\n" +
        "- Flag any sub-limits or waiting periods that could trip a claim\n" +
        "- Give you a one-page summary you can keep\n\n" +
        "It usually takes me ~30 seconds once you upload.",
    };
  }

  if (q.includes("renewal") || q.includes("renew")) {
    return {
      body:
        "You've got **TATA AIG renewing in 12 days** (₹14,200) and Niva Bupa in " +
        "48 days. Want me to:\n\n" +
        "1. **Renew as-is** — fastest, keeps your waiting-period credit\n" +
        "2. **Market check first** — compare your renewal against 6 alternatives\n\n" +
        "If switching saves real money I'll flag it, but I'll also tell you when " +
        "switching *doesn't* make sense (PED clauses reset on a new policy).",
      citations: [REGULATOR_OMBUDSMAN, INTERNAL_KNOWLEDGE],
    };
  }

  if (q.includes("claim") && (q.includes("reject") || q.includes("denied") || q.includes("file"))) {
    return {
      thinking:
        "Claims are India's #1 insurance pain. Roughly 1 in 4 first-try rejections " +
        "succeed on appeal when the documentation is right.",
      body:
        "I can help you fight this. Send me:\n\n" +
        "- The rejection letter (PDF or photo)\n" +
        "- The policy document\n" +
        "- The original hospital bill + discharge summary\n\n" +
        "I'll draft an appeal in the regulator's own language and cc IRDAI's " +
        "grievance officer. We get paid in ~14 days when the documentation is right. " +
        "No charge for this.",
    };
  }

  // Generic warm fallback.
  return {
    body:
      "Tell me more about what you're trying to figure out — I can compare plans, " +
      "audit an existing policy, draft a claim appeal, or just explain how something " +
      "in your current cover actually works. What's on your mind?",
  };
}

function inferFileName(prompt: string): string {
  const m = /([\w.-]+\.(pdf|jpg|jpeg|png))/i.exec(prompt);
  return m ? m[1] : "policy.pdf";
}

function mockOcrResult(fileName: string): OcrResultData {
  return {
    fileName,
    policyName: "TATA AIG MediCare Premier · 2024",
    highlights: [
      { label: "Sum insured", value: "Rs 10,00,000" },
      { label: "Annual premium", value: "Rs 14,200" },
      { label: "Policy term", value: "1 year, auto-renewable" },
      { label: "Insured members", value: "Self + spouse + 1 child" },
      { label: "Cashless network", value: "5,200+ hospitals" },
      { label: "Pre-policy waiting", value: "30 days (sickness)" },
    ],
    summary:
      "Good baseline floater. The **room-rent capping (1% of sum insured)** is the only material issue — in most BLR private rooms you would pay the difference out of pocket. Otherwise the cover is broad and the no-claim bonus (+50% per year, capped at 100%) is genuinely strong.",
  };
}

/**
 * Drives an in-flight assistant message from empty → full body by
 * emitting incremental deltas. Returns a cleanup function the caller
 * MUST call when the component unmounts mid-stream.
 *
 * Cadence is character-based with a small jitter so it doesn't look
 * robotic. ~16ms per tick = roughly 60 chars/s, which feels like a
 * fast model. Adjust `intervalMs` to taste.
 */
export function mockStream(
  reply: MockReply,
  callbacks: {
    onThinking?: (thinking: string) => void;
    onDelta: (deltaText: string) => void;
    /**
     * Called once the streamed body finishes. The `reply`'s `blocks`
     * and `citations` are passed back here so the caller can attach
     * them to the same Message in one state update.
     */
    onDone: (extras: {
      blocks?: MessageBlock[];
      citations?: Citation[];
    }) => void;
  },
  options: { intervalMs?: number; chunkSize?: number } = {},
): () => void {
  const intervalMs = options.intervalMs ?? 16;
  const chunkSize = options.chunkSize ?? 3;

  // Fire the thinking summary IMMEDIATELY (one shot, not streamed).
  if (reply.thinking && callbacks.onThinking) {
    callbacks.onThinking(reply.thinking);
  }

  const total = reply.body;
  let cursor = 0;
  let cancelled = false;

  const timer = window.setInterval(() => {
    if (cancelled) return;
    if (cursor >= total.length) {
      window.clearInterval(timer);
      callbacks.onDone({
        blocks: reply.blocks,
        citations: reply.citations,
      });
      return;
    }
    // Take a small slice + a jitter so the cadence varies.
    const jitter = Math.floor(Math.random() * 3);
    const next = Math.min(total.length, cursor + chunkSize + jitter);
    const delta = total.slice(cursor, next);
    cursor = next;
    callbacks.onDelta(delta);
  }, intervalMs);

  return () => {
    cancelled = true;
    window.clearInterval(timer);
  };
}

/**
 * Helper: builds an empty in-flight assistant message that the streamer
 * will fill in. Caller is responsible for appending this to the message
 * list, then calling mockStream() with the matching id.
 */
export function makeStreamingPlaceholder(): Message {
  return {
    id: crypto.randomUUID(),
    role: "assistant",
    content: "",
    streaming: true,
    createdAt: Date.now(),
  };
}
